import {Component, OnDestroy, OnInit}   from '@angular/core';
import {NgbModal, NgbModalOptions,
	NgbModalRef}                        from "@ng-bootstrap/ng-bootstrap";
import {Router}                         from "@angular/router";

import {DialogService}                  from "../../services/miscellaneous/dialog-service";
import {Message}                        from "../../services/message/message";
import {MessageEntry}                   from "../../services/message/message-entry";
import {MessageService}                 from "../../services/message/message.service";
import {Permissions}                    from "../../services/permissions/permissions";
import {Query}                          from "../../services/query/query";
import {UserEditorComponent}            from "./user-editor.component";
import {UserService}                    from "../../services/user/user.service";
import {User}                           from "../../services/user/user";

import {Subscription}                   from "rxjs/Rx";


@Component({
	selector: 'app-user-manager',
	templateUrl: './user-manager.component.html',
	styleUrls: ['./user-manager.component.css']
})


/**
 * Handle the user administration screen.
 */
export class UserManagerComponent implements OnInit, OnDestroy {

	private message: Message;
	modalRef: NgbModalRef;                          // Used by NgbModal.
	modalSubscription: Subscription;                // Used to communicate between the modal and this component.
	msgSubscription: Subscription;                  // Used by the MessageService.
	users: User[];
	userSubscription: Subscription;                 // Used by the UserService.


	/**
	 * Initialize the services needed to run the component.
	 *
	 * @param {DialogService} dialogService  Handles 'yes/no' dialogs.
	 * @param {NgbModal} modalService      Allows pop up of the editing window.
	 * @param {MessageService} msgService  Handles system messages.
	 * @param {Router} router              Used when returning to Story List screen.
	 * @param {UserService} userService    Retrieves and stores user data.
	 */
	constructor(

		private dialogService: DialogService,
		private modalService: NgbModal,
		private msgService: MessageService,
		private router: Router,
		private userService: UserService

	) {}


	/**
	 * Fire up the message service and retrieve the user list.
	 */
	ngOnInit() {

		this.msgSubscription = this.msgService.subject.subscribe( message => { this.message = message; } );
		this.showUsers();

	}


	/**
	 * Get rid of any existing subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.modalSubscription) {
			this.modalSubscription.unsubscribe();
		}

		if (this.msgSubscription) {
			this.msgSubscription.unsubscribe();
		}

		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}

	}


	/**
	 * Open a new User object in the modal editing window.
	 */
	create():void {

		let empty:User = new User();
		empty.status = 'A';
		empty.id = -1;
		empty.permissions = 1;
		this.edit(empty);

	}

	/**
	 * Delete the User record with the specified ID from the database.
	 *
	 * @param {User} user The User to delete.
	 */
	delete(user:User):void {

		this.dialogService.confirm('OK to delete ' + user.uname + '?').then(
			(response) => {
				if (response) {

					this.userSubscription = this.userService.deleteUser(user.id).subscribe(
						() => {

							this.msgService.sendMessageEntry(new MessageEntry({

								message: 'User ' + user.uname + ' successfully deleted',
								level: 1

							}));

							// Remove the user from the list being displayed.
							this.users = this.users.filter(entry => entry.id !== user.id);

						},
						(error) => {

							this.msgService.sendMessageEntry(new MessageEntry({

								message: 'User deletion failed. ' + Query.getQueryErrorMessage(error),
								level: 4

							}));

						}
					);

				}
			}
		);

	}


	/**
	 * Edit a single user in the modal dialog window.
	 *
	 * @param {User} user  The User object to edit.
	 */
	edit(user:User):void {

		let opts: NgbModalOptions = {
			size : 'lg',
		};

		this.modalRef = this.modalService.open(UserEditorComponent, opts);
		this.modalRef.componentInstance.user = user;

		// Update the list of users after a change in the modal.
		this.modalSubscription = this.modalRef.componentInstance.emitter.subscribe(
			() => {
				this.showUsers();
			}
		)

	}


	/**
	 * Returns true if the user is an administrator.  If no User
	 * is provided, it tests the current user.
	 *
	 * @param {User} user  The User object to test. (Optional)
	 * @return {boolean} True if the user is an admin, false otherwise.
	 */
	isAdministrator(user:User = null):boolean {

		return Permissions.checkPermission(Permissions.SUPER_USER, user);

	}


	/**
	 * Replace the existing user in the admin display
	 * with the new one.  The comparison is done by
	 * ID value.
	 *
	 * @param {User} replacement  The replacement User.
	 */
	replaceUser(replacement:User):void {

		let updateItem:User = this.users.find(x => x.id === replacement.id);
		let index:number = this.users.indexOf(updateItem);
		if (index > -1) {

			this.users[index] = replacement;

		}

	}

	/**
	 * Saves a User into the database.  If the save is
	 * successful, the existing user on the admin screen
	 * will be replaced with the changed User.
	 *
	 * @param {User} user   The User object to be saved.
	 * @param {string} msgSuccess  The message to display if the update works.
	 * @param {string} msgFailure  The message to display if the update fails.
	 */
	save(user:User, msgSuccess:string, msgFailure:string):void {

		this.userSubscription = this.userService.save(user).subscribe(
			(response:User) => {

				// Replace the previous user with the new one.
				this.replaceUser(response);
				this.msgService.sendMessageEntry(new MessageEntry({

					message: msgSuccess,
					level: 1

				}));


			},
			(error:any) => {

				this.msgService.sendMessageEntry(new MessageEntry({

					message: msgFailure + ' ' + Query.getQueryErrorMessage(error),
					level: 4

				}));

			}
		);

	}


	/**
	 * Populate the list of users for editing.
	 */
	showUsers():void {

		this.userSubscription = this.userService.getUsers().subscribe(
			(response:User[]) => {

				this.users = response;
				let msg:string = '';

				if (1 === response.length) {

					msg = '1 user found';

				} else {

					msg = response.length.toString() + ' users found'

				}

				this.msgService.sendMessageEntry(new MessageEntry({

					message: msg,
					level: 1

				}));

			},
			(error) => {

				this.msgService.sendMessageEntry(new MessageEntry({

					message: 'Unable to retrieve users. ' + Query.getQueryErrorMessage(error),
					level: 4

				}));

			}
		);

	}


	/**
	 * Flip the User status from its current value
	 * to the other state and save the change in the
	 * database.
	 *
	 * @param {User} user  The User to change status for.
	 */
	status(user:User):void {

		// Flip the user status before the save.
		let update:User = new User(user);
		update.status = (user.status === 'A') ? 'S' : 'A';
		this.save(update, 'Updated status for ' + user.uname,
			'Failed to update status for ' + user.uname);

	}


	/**
	 * Flip the User from an administrator to regular
	 * user (or back) and save the change in the
	 * database.
	 *
	 * @param {User} user  The User to change admin/non-admin status for.
	 */
	superuser(user:User):void {

		// Flip the user's administrative status before the save.
		let update:User = new User(user);
		update.permissions = (this.isAdministrator(user)) ? update.permissions - Permissions.SUPER_USER : update.permissions + Permissions.SUPER_USER;
		this.save(update, 'Updated administrative status for ' + user.uname,
			'Failed to update administrative status for ' + user.uname);

	}


	/**
	 * Switch back to the story list.
	 *
	 */
	viewStoryList(): void {

		this.router.navigate(['/articles/list']);

	}

}



// WEBPACK FOOTER //
// ./src/app/admin/users/user-manager.component.ts